import React, { useCallback, useMemo, useState } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import ErrorHandlerService from 'services/error-handler/service';
import { colorsTheme } from 'resources/theme/styled/colors';
import { EntityTypeOption } from './AvailableEntityTypes';
import { ApiErrorResponseModel } from 'api/axiosInstance';
import { useEntityIntakeFormContext } from '../../EntityIntakeFormContext';
import { entityTypeStepValidationSchema } from 'validations/intake-form/intake-form-steps';

// components
import InnerForm from './InnerForm';
import DivAlignCenter from 'components/Additional/DivAlignCenter';
import SectionTitleNavigateButton from '../SectionTitleNavigateButton';
import { AutocompleteOption, Form, Modal, SectionIntro } from '@ui';

interface IProps {
  disabled?: boolean;
  renderNavigateButtonInTitle?: boolean;
  renderEntityTypeUsingSelect?: boolean;
}

export interface FormValuesModel {
  countryOfIncorporation: string | null;
  entityTypeCategory: string | null;
  countryOfIncorporationInitialOption?: AutocompleteOption;
}

const EntityType = ({
  disabled,
  renderNavigateButtonInTitle,
  renderEntityTypeUsingSelect,
}: IProps) => {
  const { t } = useTranslation(['onboarding', 'common']);
  const { intakeForm, navigateToNextStep, updateIntakeFormFields } =
    useEntityIntakeFormContext();
  const [initialEntityType, setInitialEntityType] =
    useState<EntityTypeOption | null>(null);

  const initialValues = useMemo<FormValuesModel>(() => {
    if (!intakeForm) {
      return {
        entityTypeCategory: '',
        countryOfIncorporation: '',
      };
    } else {
      let countryOfIncorporationInitialOption: AutocompleteOption | undefined =
        undefined;

      if (intakeForm.metadata.keyRelationshipTemplateInstance) {
        setInitialEntityType({
          id: intakeForm.metadata.keyRelationshipTemplateInstance.id,
          label: intakeForm.metadata.keyRelationshipTemplateInstance.name,
          description:
            intakeForm.metadata.keyRelationshipTemplateInstance.description,
        });
      }

      if (
        intakeForm.fields?.entityCountry &&
        intakeForm.metadata?.countries[intakeForm?.fields?.entityCountry]
      ) {
        countryOfIncorporationInitialOption = {
          id: intakeForm.fields.entityCountry,
          label:
            intakeForm.metadata.countries[intakeForm?.fields?.entityCountry],
        };
      }

      return {
        entityTypeCategory: intakeForm?.fields?.entityTypeCategoryId || '',
        countryOfIncorporation: intakeForm?.fields?.entityCountry || '',
        countryOfIncorporationInitialOption,
      };
    }
  }, [intakeForm]);

  const handleSubmit = async (values: FormValuesModel) => {
    async function submitChangesToIntakeForm(values: FormValuesModel) {
      await updateIntakeFormFields(
        {
          entityCountry: values.countryOfIncorporation || '',
          entityTypeCategoryId: values.entityTypeCategory || '',
        },
        undefined,
      );
      navigateToNextStep();
    }

    if (intakeForm?.completionStatuses.entityType == 'complete') {
      const isSubmitted = intakeForm.state === 'submitted';

      const modalConfig = {
        title: t(
          isSubmitted
            ? 'intake_form.submit_buttons.confirmation_modal.title'
            : 'intake_form.entity_type.edit_confirmation_modal.title',
        ),
        content: t(
          isSubmitted
            ? 'intake_form.submit_buttons.confirmation_modal.description'
            : 'intake_form.entity_type.edit_confirmation_modal.description',
        ),
        okText: t(isSubmitted ? 'proceed' : 'confirm', { ns: 'common' }),
        cancelText: t('cancel', { ns: 'common' }),
      };

      Modal.confirm({
        width: 600,
        icon: null,
        closable: true,
        maskClosable: true,
        onOk: async () => {
          try {
            await submitChangesToIntakeForm(values);
          } catch (error) {
            ErrorHandlerService.handleError(
              error as PromiseRejectionEvent | ApiErrorResponseModel,
            );
          }
        },
        ...modalConfig,
      });
    } else {
      await submitChangesToIntakeForm(values);
    }
  };

  const clearInitialEntityTypeCategory = useCallback(() => {
    setInitialEntityType(null);
  }, []);

  return (
    <>
      <SectionIntro
        hideDivider
        titleColor={colorsTheme.colorWhite}
        titleVariant={disabled ? 'h5' : undefined}
        title={
          <DivAlignCenter>
            {t('intake_form.entity_type.title')}
            {renderNavigateButtonInTitle ? (
              <SectionTitleNavigateButton navigateToStepKey="entity_type" />
            ) : null}
          </DivAlignCenter>
        }
      />
      <Form
        disabled={disabled}
        onSubmit={handleSubmit}
        renderForm={
          <InnerForm
            initialEntityTypeCategory={initialEntityType}
            renderEntityTypeUsingSelect={renderEntityTypeUsingSelect}
            clearInitialEntityTypeCategory={clearInitialEntityTypeCategory}
          />
        }
        initialValues={initialValues}
        validationSchema={entityTypeStepValidationSchema}
      />
    </>
  );
};

export default EntityType;
